'use client'

import * as React from 'react'
import { DayPicker, type DropdownProps } from 'react-day-picker'

import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'

import { buttonVariants } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

import { cn } from '@/utils'

const YEAR_RANGE_START = 1970
const YEAR_RANGE_END = new Date().getFullYear() + 10

function Calendar({
  captionLayout = 'dropdown',
  className,
  classNames,
  endMonth,
  showOutsideDays = true,
  startMonth,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      captionLayout={captionLayout}
      className={cn('p-3', className)}
      classNames={{
        button_next: cn(
          buttonVariants({ variant: 'ghost' }),
          'absolute end-1 z-10 size-8 p-0 text-muted-foreground/80 hover:text-foreground'
        ),
        button_previous: cn(
          buttonVariants({ variant: 'ghost' }),
          'absolute start-1 z-10 size-8 p-0 text-muted-foreground/80 hover:text-foreground'
        ),
        caption_label: 'inline-flex items-center gap-1 text-sm font-medium',
        day: 'group size-8 px-0 py-px text-sm',
        day_button:
          'cursor-pointer relative flex size-8 items-center justify-center whitespace-nowrap rounded-md p-0 text-foreground transition-200 group-[[data-selected]:not(.range-middle)]:[transition-property:color,background-color,border-radius,box-shadow] group-[[data-selected]:not(.range-middle)]:duration-150 group-data-disabled:pointer-events-none focus-visible:z-10 hover:not-in-data-selected:bg-accent group-data-selected:bg-primary hover:not-in-data-selected:text-foreground group-data-selected:text-primary-foreground group-data-disabled:text-foreground/30 group-data-disabled:line-through group-data-outside:text-foreground/30 group-data-selected:group-data-outside:text-primary-foreground outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] group-[.range-start:not(.range-end)]:rounded-e-none group-[.range-end:not(.range-start)]:rounded-s-none group-[.range-middle]:rounded-none group-[.range-middle]:group-data-selected:bg-accent group-[.range-middle]:group-data-selected:text-foreground',
        dropdowns: 'flex items-center gap-2',
        hidden: 'invisible',
        month: 'w-full',
        month_caption: 'relative mb-1 flex h-8 items-center justify-center',
        months: 'flex flex-col sm:flex-row gap-4',
        nav: 'contents',
        outside:
          'text-muted-foreground data-selected:bg-accent/50 data-selected:text-muted-foreground',
        range_end: 'range-end',
        range_middle: 'range-middle',
        range_start: 'range-start',
        today:
          '*:after:pointer-events-none *:after:absolute *:after:bottom-1 *:after:start-1/2 *:after:z-10 *:after:size-[3px] *:after:-translate-x-1/2 rtl:*:after:translate-x-1/2 *:after:rounded-full *:after:bg-primary [&[data-selected]:not(.range-middle)>*]:after:bg-background [&[data-disabled]>*]:after:bg-foreground/30 *:after:transition-colors',
        week_number: 'size-8 p-0 text-xs font-medium text-muted-foreground/80',
        weekday: 'size-8 p-0 text-xs font-medium text-muted-foreground/80',
        ...classNames
      }}
      components={{
        Chevron: (props) => {
          if (props.orientation === 'left') {
            return <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
          } else if (props.orientation === 'right') {
            return <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          } else {
            return <ChevronDown className="h-4 w-4" />
          }
        },
        MonthsDropdown: MonthsGridDropdown,
        YearsDropdown: YearsSelectDropdown
      }}
      endMonth={endMonth ?? new Date(YEAR_RANGE_END, 11)}
      showOutsideDays={showOutsideDays}
      startMonth={startMonth ?? new Date(YEAR_RANGE_START, 0)}
      {...props}
    />
  )
}

function fireSelectChange(
  onChange: DropdownProps['onChange'],
  value: number | string
) {
  if (!onChange) return
  const event = {
    target: { value: String(value) }
  } as unknown as React.ChangeEvent<HTMLSelectElement>
  onChange(event)
}

function MonthsGridDropdown({
  disabled,
  onChange,
  options = [],
  value
}: DropdownProps) {
  const [open, setOpen] = React.useState(false)
  const selected = options.find((o) => o.value === value)
  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        className={cn(
          buttonVariants({ variant: 'outline' }),
          'relative h-7 cursor-pointer gap-1 px-2 text-sm font-medium'
        )}
        disabled={disabled}
      >
        <span>{selected?.label ?? ''}</span>
        <ChevronDown className="h-4 w-4 opacity-60" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-2 ltr:-ms-10 rtl:-ms-8.5"
      >
        <div className="grid grid-cols-3 gap-1">
          {options.map((option) => {
            const isSelected = option.value === value
            return (
              <button
                className={cn(
                  'rounded-md px-1 py-1.5 text-xs font-medium transition-colors',
                  'hover:bg-accent hover:text-foreground',
                  'disabled:pointer-events-none disabled:opacity-50',
                  isSelected
                    ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'
                    : 'text-foreground'
                )}
                disabled={option.disabled}
                key={option.value}
                onClick={() => {
                  fireSelectChange(onChange, option.value)
                  setOpen(false)
                }}
                type="button"
              >
                {option.label}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function YearsSelectDropdown({
  disabled,
  onChange,
  options = [],
  value
}: DropdownProps) {
  return (
    <Select
      disabled={disabled}
      onValueChange={(v) => fireSelectChange(onChange, v)}
      value={value !== undefined ? String(value) : undefined}
    >
      <SelectTrigger
        className={
          (buttonVariants({ variant: 'outline' }),
          'relative h-7 cursor-pointer gap-1 px-2 text-sm font-medium')
        }
        size="sm"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {options.map((option) => (
          <SelectItem
            disabled={option.disabled}
            key={option.value}
            value={String(option.value)}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export { Calendar }
